package com.gavino.nutritrack

import kotlin.math.max
import kotlin.math.roundToInt

enum class Gender(val label: String, val mifflinOffset: Int) {
    MALE("Uomo", 5),
    FEMALE("Donna", -161)
}

enum class ActivityLevel(val label: String, val description: String, val multiplier: Double) {
    SEDENTARY("Sedentario (Ufficio)", "Lavoro da scrivania, spostamenti in auto/scooter, poco esercizio", 1.20),
    LIGHT("Leggermente Attivo", "Camminata leggera o 1-2 allenamenti leggeri a settimana", 1.375),
    MODERATE("Moderatamente Attivo", "3-4 allenamenti a settimana o lavoro dinamico", 1.55),
    ACTIVE("Molto Attivo", "Allenamento intenso 5-6 volte a settimana", 1.725)
}

enum class DeficitGoal(val label: String, val dailyDeficitKcal: Int, val weeklyKgLoss: Double) {
    MAINTENANCE("Mantenimento (0 kcal)", 0, 0.0),
    MILD_LOSS("Dimagrimento Dolce (-350 kcal/dì)", 350, 0.32),
    STANDARD_LOSS("Dimagrimento Ideale (-500 kcal/dì)", 500, 0.45),
    FAST_LOSS("Dimagrimento Rapido (-650 kcal/dì)", 650, 0.60)
}

data class UserProfileData(
    val gender: Gender = Gender.MALE,
    val age: Int = 42,
    val heightCm: Int = 175,
    val currentWeightKg: Double = 84.0,
    val targetWeightKg: Double = 74.0,
    val waistCm: Int = 92,
    val activityLevel: ActivityLevel = ActivityLevel.SEDENTARY,
    val deficitGoal: DeficitGoal = DeficitGoal.STANDARD_LOSS
)

data class MetabolicReport(
    val bmi: Double,
    val bmiCategory: String,
    val idealWeightMinKg: Double,
    val idealWeightMaxKg: Double,
    val waistToHeightRatio: Double,
    val waistRiskCategory: String,
    val bmrKcal: Int,
    val tdeeKcal: Int,
    val recommendedDietKcal: Int,
    val recommendedCarbGrams: Int,
    val recommendedProtGrams: Int,
    val recommendedFatGrams: Int,
    val recommendedWaterMl: Int,
    val recommendedWaterGlasses: Int,
    val kgToLose: Double,
    val estimatedWeeksToGoal: Int
)

object UserProfileEngine {

    fun calculateMetrics(profile: UserProfileData): MetabolicReport {
        val heightM = (profile.heightCm.coerceAtLeast(100)) / 100.0
        val weight = profile.currentWeightKg.coerceAtLeast(35.0)
        val targetW = profile.targetWeightKg.coerceAtLeast(35.0)

        // 1. Calcolo BMI e Peso Ideale (BMI 18.5 - 24.9)
        val rawBmi = weight / (heightM * heightM)
        val bmi = (rawBmi * 10.0).roundToInt() / 10.0
        val bmiCat = when {
            bmi < 18.5 -> "Sottopeso"
            bmi < 25.0 -> "Normopeso"
            bmi < 30.0 -> "Sovrappeso"
            bmi < 35.0 -> "Obesità Grado I"
            else -> "Obesità Grado II+"
        }

        val idealMin = ((18.5 * heightM * heightM) * 10.0).roundToInt() / 10.0
        val idealMax = ((24.9 * heightM * heightM) * 10.0).roundToInt() / 10.0

        // 2. Rapporto Girovita / Altezza (WHtR - indice rischio metabolico e insulino-resistenza)
        val rawWhtr = profile.waistCm.toDouble() / profile.heightCm.toDouble()
        val whtr = (rawWhtr * 100.0).roundToInt() / 100.0
        val waistCat = when {
            whtr < 0.50 -> "Ottimale (Basso rischio metabolico)"
            whtr < 0.60 -> "Attenzione (Grasso viscerale moderato)"
            else -> "Elevato (Rischio metabolico alto)"
        }

        // 3. Metabolismo Basale (BMR - Equazione Mifflin-St Jeor)
        val bmr = ((10.0 * weight) + (6.25 * profile.heightCm) - (5.0 * profile.age) + profile.gender.mifflinOffset).roundToInt()

        // 4. TDEE (Total Daily Energy Expenditure)
        val tdee = (bmr * profile.activityLevel.multiplier).roundToInt()

        // 5. Calorie Target consigliate per il dimagrimento (con soglia minima di sicurezza)
        val minSafeKcal = if (profile.gender == Gender.MALE) 1300 else 1150
        val recKcal = max(minSafeKcal, tdee - profile.deficitGoal.dailyDeficitKcal)

        // 6. Ripartizione Macronutrienti per dimagrimento salvamusa-muscolare:
        // Proteine: ~1.6g per kg di peso ideale/obiettivo per preservare la massa magra
        // Grassi: ~26% delle calorie totali
        // Carboidrati: quota calorica restante a basso indice glicemico
        val refWeightForProt = if (weight > idealMax) idealMax else weight
        val recProt = (refWeightForProt * 1.65).roundToInt()
        val recFat = ((recKcal * 0.26) / 9.0).roundToInt()
        val remainingCalForCarb = max(200, recKcal - (recProt * 4) - (recFat * 9))
        val recCarb = (remainingCalForCarb / 4.0).roundToInt()

        // 7. Fabbisogno Idrico (35 ml per kg di peso corporeo)
        val waterMl = ((weight * 35.0) / 100.0).roundToInt() * 100
        val waterGlasses = max(6, (waterMl / 250.0).roundToInt())

        // 8. Stima Settimane per raggiungere il Peso Obiettivo (1 kg di tessuto adiposo ≈ 7000 kcal)
        val kgDiff = ((weight - targetW) * 10.0).roundToInt() / 10.0
        val actualDailyDeficit = (tdee - recKcal).coerceAtLeast(0)
        val weeks = if (kgDiff > 0 && actualDailyDeficit >= 150) {
            val totalKcalToBurn = kgDiff * 7000.0
            val daysNeeded = totalKcalToBurn / actualDailyDeficit.toDouble()
            max(1, (daysNeeded / 7.0).roundToInt())
        } else 0

        return MetabolicReport(
            bmi = bmi,
            bmiCategory = bmiCat,
            idealWeightMinKg = idealMin,
            idealWeightMaxKg = idealMax,
            waistToHeightRatio = whtr,
            waistRiskCategory = waistCat,
            bmrKcal = bmr,
            tdeeKcal = tdee,
            recommendedDietKcal = recKcal,
            recommendedCarbGrams = recCarb,
            recommendedProtGrams = recProt,
            recommendedFatGrams = recFat,
            recommendedWaterMl = waterMl,
            recommendedWaterGlasses = waterGlasses,
            kgToLose = max(0.0, kgDiff),
            estimatedWeeksToGoal = weeks
        )
    }
}